import { useCallback, useEffect, useRef, useState } from 'react';
import type { CharacterDraft, CharacterVisit, WorldDraft } from '../../domain/types.js';
import type { CharacterBodySnapshot } from './characterBody.js';
import { bodyFromCharacterDraft } from './characterBody.js';
import CharacterBasicScreen from './character/CharacterBasicScreen.js';
import CharacterDetailedScreen, { ensureDetailedDefaults } from './character/CharacterDetailedScreen.js';
import CharacterTraitsScreen from './character/CharacterTraitsScreen.js';
import AiPreviewDialog from './character/AiPreviewDialog.js';
import {
  suggestBasicIdentity,
  suggestDetailedLook,
  suggestNature,
  suggestTrait,
} from './character/aiSuggestions.js';
import {
  activeCharacters,
  addCharacterToDraft,
  mergeWorking,
  newWorkingCharacter,
  patchCharacterInDraft,
  removeCharacterFromDraft,
} from './character/draftOps.js';
import { validateBasic, canSaveAndContinue, validateTraits } from './character/validation.js';
import RelationshipWire from './RelationshipWire.js';
import { rollVisits } from '../../runtime/visitPresence.js';

export type CharacterPhase = 'basic' | 'editing' | 'detailed' | 'traits';

export interface CharacterPopulationAdapterProps {
  draft: WorldDraft;
  bodies: Record<string, CharacterBodySnapshot>;
  onDraftChange: (draft: WorldDraft) => void;
  onBodiesChange: (bodies: Record<string, CharacterBodySnapshot>) => void;
  onBack: () => void;
  onSaveToast: () => void;
  onReset: () => void;
  /** Called after Save and Continue when roster has ≥1 complete character. */
  onCharacterFlowComplete?: () => void;
  /** Aquarium return: saving a new character puts them in the location and leaves. */
  onSavedToLocation?: (draft: WorldDraft, bodies: Record<string, CharacterBodySnapshot>) => void;
  /** Sub-phase within character-population (basic → detailed → traits). */
  onPhaseChange?: (phase: CharacterPhase) => void;
  /** When set, open that character in edit mode on mount (wizard review → edit). */
  startEditId?: string | null;
  onStartEditConsumed?: () => void;
  /** Wizard footer is the only Continue. Null run means this substep is not mid-character. */
  shellOwnsContinue?: boolean;
  onRegisterContinue?: (run: (() => void) | null, disabled: boolean) => void;
  /** Wizard footer Back within character sub-phases. Null run means leave-step Back. */
  onRegisterBack?: (run: (() => void) | null) => void;
  /** Wizard reads the open character at Save World, including fields not yet on the draft. */
  captureWorking?: { current: (() => CharacterDraft | null) | null };
}

type AiPreview =
  | { kind: 'basic'; basic: CharacterDraft['basic'] }
  | { kind: 'detailed'; appearance: NonNullable<CharacterDraft['appearance']>; nature: NonNullable<CharacterDraft['nature']> }
  | { kind: 'traits'; traits: CharacterDraft['traits'] };

export default function CharacterPopulationAdapter(props: CharacterPopulationAdapterProps): JSX.Element {
  const saved = activeCharacters(props.draft);
  const [phase, setPhase] = useState<CharacterPhase>('basic');
  const [working, setWorking] = useState<CharacterDraft>(() => newWorkingCharacter());
  const [persistedId, setPersistedId] = useState<string | null>(null);
  const persistedIdRef = useRef(persistedId);
  persistedIdRef.current = persistedId;
  const editBodySnapshotRef = useRef<CharacterBodySnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiPreview, setAiPreview] = useState<AiPreview | null>(null);
  const [sex, setSex] = useState<'M' | 'F'>('M');
  const sexRef = useRef<'M' | 'F'>('M');
  const workingRef = useRef(working);
  workingRef.current = working;
  if (props.captureWorking) {
    props.captureWorking.current = () => {
      const w = workingRef.current;
      if (!w.basic.firstName.trim()) return null;
      const body = props.bodies[w.draftCharacterId];
      return {
        ...w,
        sex: w.sex ?? sexRef.current,
        species: w.species ?? body?.species ?? 'Human',
        ageRange: w.ageRange ?? body?.ageRange ?? 'Adult',
      };
    };
  }

  useEffect(() => {
    props.onPhaseChange?.(phase);
  }, [phase, props.onPhaseChange]);

  const syncBody = useCallback((character: CharacterDraft) => {
    const species = character.species ?? props.bodies[character.draftCharacterId]?.species ?? 'Human';
    const sex = character.sex ?? sexRef.current;
    const body = bodyFromCharacterDraft(character, species, sex);
    if (!body) return;
    props.onBodiesChange({ ...props.bodies, [character.draftCharacterId]: body });
  }, [props]);

  const persistWorking = useCallback((character: CharacterDraft): WorldDraft => {
    const id = persistedId ?? character.draftCharacterId;
    const prevBody = props.bodies[id];
    const payload = {
      basic: character.basic,
      appearance: character.appearance,
      nature: character.nature,
      traits: character.traits,
      source: character.source,
      sex: character.sex ?? sexRef.current,
      species: character.species ?? prevBody?.species ?? 'Human',
      background: (character.background ?? '').trim() || undefined,
      ageRange: character.ageRange ?? prevBody?.ageRange ?? 'Adult',
      visits: character.visits,
      visitor: character.visitor,
    };
    const stamped = { ...character, ...payload, draftCharacterId: id };
    if (persistedId) {
      const next = patchCharacterInDraft(props.draft, persistedId, payload);
      props.onDraftChange(next);
      syncBody(stamped);
      return next;
    }
    const next = addCharacterToDraft(props.draft, { ...payload, draftCharacterId: id });
    const added = activeCharacters(next).find((c) => c.draftCharacterId === id) ?? activeCharacters(next).at(-1)!;
    setPersistedId(added.draftCharacterId);
    syncBody({ ...stamped, draftCharacterId: added.draftCharacterId });
    props.onDraftChange(next);
    return next;
  }, [persistedId, props, syncBody]);

  function handleVisitsChange(characterId: string, visits: CharacterVisit[], visitor?: boolean): void {
    props.onDraftChange({
      ...props.draft,
      characterDrafts: props.draft.characterDrafts.map((character) =>
        character.draftCharacterId === characterId
          ? { ...character, visits: rollVisits(visits), visitor: visitor ?? character.visitor }
          : character,
      ),
    });
  }

  function applySex(next: 'M' | 'F'): void {
    sexRef.current = next;
    setSex(next);
    const id = workingRef.current.draftCharacterId;
    setWorking((w) => ({ ...w, sex: next }));
    const prev = props.bodies[id];
    if (!prev || prev.sex === next) return;
    props.onBodiesChange({ ...props.bodies, [id]: { ...prev, sex: next } });
  }

  function resetWorking(): void {
    editBodySnapshotRef.current = null;
    setWorking(newWorkingCharacter());
    setPersistedId(null);
    setError(null);
    sexRef.current = 'M';
    setSex('M');
    setPhase('basic');
  }

  function loadForEdit(character: CharacterDraft): void {
    const body = props.bodies[character.draftCharacterId];
    const appearance = character.appearance ?? (body ? {
      skinColor: body.palette.skin,
      topStyle: body.appearanceStyles?.topStyle ?? 'shirt',
      topColor: body.palette.shirt,
      bottomStyle: body.appearanceStyles?.bottomStyle ?? 'trousers',
      bottomColor: body.palette.pants,
      hairStyle: body.appearanceStyles?.hairStyle ?? 'short',
      hairColor: body.palette.hair,
    } : null);
    const nature = character.nature ?? (body ? { ...body.nature } : null);
    const traits = character.traits.length > 0
      ? character.traits
      : (body?.nurture ?? []).map((t) => ({
        traitKey: t.trait_key,
        strength: t.strength,
        reason: t.reason,
      }));
    const next = {
      ...character,
      appearance,
      nature,
      traits,
      sex: character.sex ?? body?.sex ?? 'M',
      species: character.species ?? body?.species ?? 'Human',
      ageRange: character.ageRange ?? body?.ageRange ?? 'Adult',
    };
    workingRef.current = next;
    editBodySnapshotRef.current = body
      ? {
        ...body,
        palette: { ...body.palette },
        nature: { ...body.nature },
        nurture: [...body.nurture],
        appearanceStyles: body.appearanceStyles ? { ...body.appearanceStyles } : undefined,
      }
      : null;
    setWorking(next);
    setPersistedId(character.draftCharacterId);
    applySex(next.sex ?? 'M');
    setError(null);
    setPhase('editing');
  }

  useEffect(() => {
    if (!props.startEditId) return;
    const found = saved.find((c) => c.draftCharacterId === props.startEditId);
    if (found) loadForEdit(found);
    props.onStartEditConsumed?.();
  }, [props.startEditId]);

  const onRegisterContinue = props.onRegisterContinue;
  const onRegisterBack = props.onRegisterBack;
  const shellOwnsContinue = props.shellOwnsContinue === true;
  useEffect(() => {
    if (!shellOwnsContinue || !onRegisterBack) return;
    if (phase === 'detailed') {
      onRegisterBack(() => {
        setError(null);
        setPhase(persistedIdRef.current ? 'editing' : 'basic');
      });
      return;
    }
    if (phase === 'traits') {
      onRegisterBack(() => {
        setError(null);
        setPhase('detailed');
      });
      return;
    }
    if (phase === 'editing') {
      onRegisterBack(() => resetWorking());
      return;
    }
    onRegisterBack(null);
  }, [shellOwnsContinue, onRegisterBack, phase]);

  useEffect(() => {
    if (!shellOwnsContinue || !onRegisterContinue) return;
    if (phase === 'detailed') return;
    if (phase === 'traits') {
      onRegisterContinue(null, true);
      return;
    }
    const editing = phase === 'editing';
    const showForm = saved.length < 10 || editing;
    if (!showForm) {
      onRegisterContinue(null, true);
      return;
    }
    onRegisterContinue(goDetailed, validateBasic(working.basic) !== null);
  }, [shellOwnsContinue, onRegisterContinue, phase, saved.length, working.basic]);

  function goDetailed(): void {
    const err = validateBasic(workingRef.current.basic);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setWorking((w) => {
      const filled = ensureDetailedDefaults(w.appearance, w.nature);
      return mergeWorking(w, filled);
    });
    setPhase('detailed');
  }

  function goTraits(): void {
    setError(null);
    setPhase('traits');
  }

  function handleSaveAndContinue(): void {
    if (!canSaveAndContinue(working)) {
      setError('Complete basic identity and detailed look before continuing.');
      return;
    }
    const traitErr = validateTraits(working.traits);
    if (traitErr) {
      setError(traitErr);
      return;
    }
    persistWorking(working);
    props.onSaveToast();
    resetWorking();
    props.onCharacterFlowComplete?.();
  }

  function handleSaveAndAdd(): void {
    if (!canSaveAndContinue(working)) {
      setError('Complete basic identity and detailed look before saving.');
      return;
    }
    const traitErr = validateTraits(working.traits);
    if (traitErr) {
      setError(traitErr);
      return;
    }
    const wasEdit = persistedId != null;
    const next = persistWorking(working);
    if (!wasEdit && props.onSavedToLocation) {
      const added = activeCharacters(next).find((c) => c.draftCharacterId === working.draftCharacterId)
        ?? activeCharacters(next).at(-1);
      const body = added ? bodyFromCharacterDraft(added) : null;
      const bodies = added && body
        ? { ...props.bodies, [added.draftCharacterId]: body }
        : props.bodies;
      props.onSavedToLocation(next, bodies);
      return;
    }
    props.onSaveToast();
    resetWorking();
  }

  function handleCancelTraits(): void {
    const id = persistedId;
    const snapshot = editBodySnapshotRef.current;
    resetWorking();
    if (id && snapshot) props.onBodiesChange({ ...props.bodies, [id]: snapshot });
  }

  function acceptAiPreview(): void {
    if (!aiPreview) return;
    if (aiPreview.kind === 'basic') {
      setWorking((w) => mergeWorking(w, { basic: aiPreview.basic, source: 'ai_suggestion' }));
    } else if (aiPreview.kind === 'detailed') {
      setWorking((w) => mergeWorking(w, {
        appearance: aiPreview.appearance,
        nature: aiPreview.nature,
        source: 'ai_suggestion',
      }));
    } else {
      setWorking((w) => mergeWorking(w, { traits: aiPreview.traits, source: 'ai_suggestion' }));
    }
    setAiPreview(null);
  }

  const aiDialog = aiPreview ? (
    <AiPreviewDialog
      title="Let AI fill in the blanks"
      onAccept={acceptAiPreview}
      onCancel={() => setAiPreview(null)}
    >
      {aiPreview.kind === 'basic' && (
        <p>{aiPreview.basic.firstName}{aiPreview.basic.lastInitial ? ` ${aiPreview.basic.lastInitial}.` : ''}</p>
      )}
      {aiPreview.kind === 'detailed' && (
        <p className="world-muted">
          Look: {aiPreview.appearance.topStyle}, {aiPreview.appearance.hairStyle}. Nature sliders adjusted.
        </p>
      )}
      {aiPreview.kind === 'traits' && (
        <p className="world-muted">{aiPreview.traits.length} trait suggestion(s).</p>
      )}
    </AiPreviewDialog>
  ) : null;

  if (phase === 'detailed') {
    const { appearance, nature } = ensureDetailedDefaults(working.appearance, working.nature);
    const species = working.species ?? props.bodies[working.draftCharacterId]?.species ?? 'Human';
    const handleSpeciesChange = (nextSpecies: string): void => {
      setWorking((w) => ({ ...w, species: nextSpecies }));
      const id = working.draftCharacterId;
      const prev = props.bodies[id];
      props.onBodiesChange({
        ...props.bodies,
        [id]: prev
          ? { ...prev, species: nextSpecies, sex: sexRef.current }
          : {
            species: nextSpecies,
            sex: sexRef.current,
            ageRange: 'Adult',
            palette: {
              skin: appearance.skinColor,
              shirt: appearance.topColor,
              pants: appearance.bottomColor,
              hair: appearance.hairColor,
            },
            nature: { ...nature },
            nurture: [],
          },
      });
    };
    return (
      <>
        <CharacterDetailedScreen
          draft={props.draft}
          species={species}
          draftCharacterId={working.draftCharacterId}
          sex={sex}
          appearance={appearance}
          nature={nature}
          error={error}
          onSpeciesChange={handleSpeciesChange}
          background={working.background ?? ''}
          onBackgroundChange={(value) => setWorking((w) => mergeWorking(w, { background: value }))}
          onAppearanceChange={(a) => setWorking((w) => mergeWorking(w, { appearance: a }))}
          onNatureChange={(n) => setWorking((w) => mergeWorking(w, { nature: n }))}
          onAiSuggest={() => setAiPreview({
            kind: 'detailed',
            appearance: suggestDetailedLook(props.draft, species),
            nature: suggestNature(props.draft, working.draftCharacterId),
          })}
          onBack={() => { setError(null); setPhase(persistedId ? 'editing' : 'basic'); }}
          onSaveToast={props.onSaveToast}
          onReset={props.onReset}
          onFillNature={() => setWorking((w) => mergeWorking(w, {
            nature: suggestNature(props.draft, working.draftCharacterId),
          }))}
          onValidationError={setError}
          onContinue={goTraits}
          revealSavedNature={persistedId != null}
          shellOwnsContinue={shellOwnsContinue}
          onRegisterContinue={onRegisterContinue}
        />
        {aiDialog}
      </>
    );
  }

  if (phase === 'traits') {
    return (
      <>
        <CharacterTraitsScreen
          traits={working.traits}
          savedCount={saved.length}
          error={error}
          onTraitsChange={(traits) => setWorking((w) => mergeWorking(w, { traits }))}
          onAiSuggest={() => {
            const next = working.traits.length < 5
              ? [...working.traits, suggestTrait(props.draft, working.draftCharacterId, working.traits.length)]
              : working.traits.map((t, i) => (i === 0 ? suggestTrait(props.draft, working.draftCharacterId, 0) : t));
            setAiPreview({ kind: 'traits', traits: next });
          }}
          onBack={() => { setError(null); setPhase('detailed'); }}
          onSaveToast={props.onSaveToast}
          onReset={props.onReset}
          onCancel={handleCancelTraits}
          onSaveAndAdd={handleSaveAndAdd}
          onSaveAndContinue={handleSaveAndContinue}
          editing={persistedId != null}
        />
        {aiDialog}
      </>
    );
  }

  return (
    <>
      {/* These rows stay on this screen. Adjust renames the button. Do not unmount them. */}
      {props.onSavedToLocation && saved.length >= 2 && (
        <RelationshipWire
          characters={saved}
          bodies={props.bodies}
          bonds={props.draft.characterBonds ?? []}
          adjustRelationships
          onVisitsChange={handleVisitsChange}
          onChange={(bonds) => props.onDraftChange({ ...props.draft, characterBonds: bonds })}
          onEditCharacter={(id) => {
            const found = saved.find((c) => c.draftCharacterId === id);
            if (found) loadForEdit(found);
          }}
        />
      )}
      <CharacterBasicScreen
        editing={phase === 'editing'}
        basic={working.basic}
        saved={saved}
        error={error}
        onChange={(basic) => setWorking((w) => mergeWorking(w, { basic }))}
        onEditSaved={(id) => {
          const found = saved.find((c) => c.draftCharacterId === id);
          if (found) loadForEdit(found);
        }}
        onRemoveSaved={(id) => {
          const name = saved.find((c) => c.draftCharacterId === id)?.basic.firstName ?? 'this character';
          if (!window.confirm(`Remove "${name}"? This cannot be undone.`)) return;
          props.onDraftChange(removeCharacterFromDraft(props.draft, id));
          const nextBodies = { ...props.bodies };
          delete nextBodies[id];
          props.onBodiesChange(nextBodies);
          if (persistedId === id) resetWorking();
        }}
        onAiSuggest={() => setAiPreview({
          kind: 'basic',
          basic: suggestBasicIdentity(props.draft, saved.length),
        })}
        onBack={props.onBack}
        onSaveToast={props.onSaveToast}
        onReset={props.onReset}
        onContinue={goDetailed}
        sex={sex}
        onSexChange={applySex}
        shellOwnsContinue={shellOwnsContinue}
      />
      {aiDialog}
    </>
  );
}
